<script lang="ts">
  import BaseScene from "@engine/BaseScene.svelte";
  import { TRADEMARK_WORDMARK, IMPRINT_ATTRIBUTION, COPYRIGHT_LINE } from "@config/constants/branding";
  import { EVENTS } from "@config/constants/events";
  import gameInfo from "@config/game_info.json";

  export let onPlay: () => void;
  export let onInstall: () => void;
  export let onDownload: () => void;
  export let installAvailable = false;

  function handleInstall(): void {
    window.dispatchEvent(new CustomEvent(EVENTS.INSTALL_ACCEPTED));
    onInstall();
  }

  function handleDownload(): void {
    window.dispatchEvent(new CustomEvent(EVENTS.DOWNLOAD_STARTED));
    onDownload();
  }

  // Hide images that fail to load so the layout doesn't render a broken-image icon.
  // Placeholder art will 404 until real illustrations are produced; the wordmark heading
  // stays visible so the trademark is never invisible on the specimen page.
  function hideOnError(e: Event): void {
    const el = e.currentTarget as HTMLImageElement | null;
    if (el) el.style.display = "none";
  }

  // gameInfo.productSubtitle already excludes the brand prefix. Example values:
  // "Ocean Match" for Popphin,
  // "Wildlife Match" for Billy. Do NOT re-derive from product string: multi-word brands break.
</script>

<BaseScene sceneId="landing" ariaLabel="{gameInfo.product} landing page">
  <div class="landing">
    <header class="landing__header">
      <img
        src={gameInfo.wordmarkPath}
        alt="{gameInfo.wordmarkAltPrefix} wordmark"
        class="landing__wordmark"
        on:error={hideOnError}
      />
      <h1 class="landing__title">{TRADEMARK_WORDMARK} {gameInfo.productSubtitle}</h1>
      <p class="landing__tagline">{gameInfo.productTagline}</p>
    </header>

    <div class="landing__splash">
      <img
        src={gameInfo.splashImagePath}
        alt={gameInfo.splashImageAlt}
        loading="eager"
        on:error={hideOnError}
      />
    </div>

    <nav class="landing__actions" aria-label="Play or install the game">
      <button
        type="button"
        class="landing__btn landing__btn--primary"
        on:click={onPlay}
      >
        Play Game
      </button>

      <button
        type="button"
        class="landing__btn landing__btn--secondary"
        on:click={handleInstall}
        disabled={!installAvailable}
        aria-describedby="install-help"
      >
        Install Game
      </button>

      <a
        class="landing__btn landing__btn--secondary"
        href={gameInfo.downloadZipPath}
        download
        on:click={handleDownload}
      >
        Download Game
      </a>
    </nav>

    <p id="install-help" class="landing__help">
      {#if !installAvailable}
        Install may not be available in this browser. Try Chrome, Edge, or Safari for install support.
      {:else}
        Install as an app on your device. Works without internet after install.
      {/if}
    </p>

    <footer class="landing__footer">
      <p class="landing__imprint">{IMPRINT_ATTRIBUTION}</p>
      <p class="landing__copyright">{COPYRIGHT_LINE}</p>
      <p class="landing__legal">
        {TRADEMARK_WORDMARK} is a trademark of Cinderpoint Systems LLC.
      </p>
    </footer>
  </div>
</BaseScene>

<style>
  .landing {
    max-width: 960px;
    margin: 0 auto;
    padding: 2rem 1rem;
    text-align: center;
    font-family: var(--font-body);
  }
  .landing__header {
    margin-bottom: 2rem;
  }
  .landing__wordmark {
    max-width: 400px;
    width: 80%;
    height: auto;
  }
  .landing__title {
    font-size: 2rem;
    margin: 1rem 0 0.5rem;
    color: var(--color-heading);
  }
  .landing__tagline {
    font-size: 1.25rem;
    color: var(--color-body);
  }
  .landing__splash {
    margin-bottom: 2rem;
  }
  .landing__splash img {
    max-width: 100%;
    height: auto;
    border-radius: 1rem;
  }
  .landing__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  .landing__btn {
    min-width: 200px;
    min-height: 44px;
    padding: 0.75em 1.5em;
    font-family: var(--font-body);
    font-size: 1.25rem;
    font-weight: 600;
    border: 2px solid var(--color-accent);
    border-radius: 0.5em;
    background: var(--color-surface);
    color: var(--color-accent);
    cursor: pointer;
    text-decoration: none;
    transition: transform 100ms;
  }
  .landing__btn--primary {
    background: var(--color-accent);
    color: var(--color-on-accent);
  }
  .landing__btn:hover:not(:disabled) {
    transform: translateY(-2px);
  }
  .landing__btn:focus-visible {
    outline: 3px solid var(--focus-ring);
    outline-offset: 2px;
  }
  .landing__btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .landing__help {
    font-size: 0.9rem;
    color: var(--color-muted);
    margin-bottom: 2rem;
  }
  .landing__footer {
    border-top: 1px solid var(--color-divider);
    padding-top: 1rem;
    color: var(--color-muted);
    font-size: 0.9rem;
  }
  .landing__imprint {
    font-weight: 600;
  }
</style>
